import React, { useState, useEffect } from 'react';
import { Modal, Row, Col, Form } from 'react-bootstrap';
import Button from 'components/base/Button';
import { useHVAConversation } from './HVAProductAndOrdersContext';
import HVADynamicConversationWidget from './HVAProductAndOrdersWidget';
import {
  HVADynamicConversationModalProps,
  FeedbackMessage,
  CATALOG_DATA
} from './HVAProductAndOrdersData';
import OrdersList from './OrdersList';
import ProductsList from './ProductsList';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

const HVADynamicConversationModalContent: React.FC<
  HVADynamicConversationModalProps
> = ({
  show,
  onHide,
  title,
  agentCode,
  agentName,
  agentImage,
  onError,
  customerId,
  email,
  name,
  firstMessage,
  prompt,
  voice,
  language
}) => {
  const {
    setClientInfo,
    isCustomerIdValidated,
    isEmailValidated,
    requestTransfer,
    setRequestTransfer,
    setIsCustomerIdValidated,
    setIsEmailValidated,
    setProducts,
    setOrders
  } = useHVAConversation();

  // Inicializar clientInfo cuando se montan los props
  useEffect(() => {
    if (customerId && email && name) {
      setClientInfo({
        customerId,
        email,
        name
      });
    }
  }, [customerId, email, name, setClientInfo]);

  const [formData, setFormData] = useState({
    customerId: customerId || '',
    email: email || '',
    name: name || ''
  });

  const [feedbackMessages, setFeedbackMessages] = useState<FeedbackMessage[]>(
    []
  );

  // Cargar datos del catálogo
  useEffect(() => {
    setProducts(CATALOG_DATA.productos);
    setOrders(CATALOG_DATA.pedidos);
  }, [setProducts, setOrders]);

  // Función para añadir mensajes de feedback
  const addFeedbackMessage = (
    message: Omit<FeedbackMessage, 'id' | 'timestamp'>
  ) => {
    setFeedbackMessages(prev => [
      {
        ...message,
        id: Math.random().toString(36).substring(2, 11),
        timestamp: new Date()
      },
      ...prev
    ]); // Añadimos el nuevo mensaje al principio del array
  };

  // Función para resetear el estado
  const resetState = () => {
    setFormData({
      customerId: customerId || '',
      email: email || '',
      name: name || ''
    });
    setFeedbackMessages([]);
    setIsCustomerIdValidated(false);
    setIsEmailValidated(false);
    setRequestTransfer(false);
    setClientInfo({
      customerId: customerId || '',
      email: email || '',
      name: name || ''
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLElement>) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const { name, value } = target;

    // Manejo especial para el tipo de préstamo
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    setClientInfo({
      customerId: formData.customerId,
      email: formData.email,
      name: formData.name
    });

    setIsCustomerIdValidated(false);
    setIsEmailValidated(false);

    setFeedbackMessages(prev => [
      {
        id: Math.random().toString(36).substring(2, 11),
        timestamp: new Date(),
        type: 'validation',
        message: 'Customer information updated',
        details: `Customer ID: ${formData.customerId} .... Email: ${formData.email}`,
        success: true
      },
      ...prev
    ]);
  };

  const handleCloseModal = () => {
    resetState();
    onHide();
  };

  const handleTransfer = async () => {
    try {
      // Resetear todos los estados
      resetState();

      // Añadir mensaje de feedback sobre la transferencia
      const newFeedbackMessage = {
        id: Math.random().toString(36).substring(2, 11),
        timestamp: new Date(),
        type: 'close',
        message: 'Llamada finalizada',
        details: `Customer ID: ${formData.customerId}`,
        success: true
      } as FeedbackMessage;

      setFeedbackMessages(messages => [newFeedbackMessage, ...messages]);
    } catch (error) {
      console.error('Error al transferir la llamada:', error);
      onError?.(error as Error);
    }
  };

  const renderTransferAlert = () => {
    if (!requestTransfer) return null;

    return (
      <div className="d-flex align-items-center fs-9 pt-2">
        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
        <div className="flex-grow-1">Request to Transfer Call</div>
        <Button variant="warning" size="sm" onClick={handleTransfer}>
          Transfer Call
        </Button>
      </div>
    );
  };

  const renderFeedbackPanel = () => {
    return (
      <>
        <h5 className="pb-0 pt-4">{`${agentName}'s feedback`}</h5>
        <p className="fs-9 lh-sm mb-2">
          The panel below displays requests for information or tool usage made
          during the conversation.
        </p>
        <div
          className="p-3 pt-0 border rounded scrollbar bg-body-emphasis shadow-sm"
          style={{
            height: '176px',
            overflowY: 'auto',
            scrollBehavior: 'smooth'
          }}
        >
          {renderTransferAlert()}

          {/* Mensajes de feedback de las tools */}
          <div className="mt-3">
            {feedbackMessages.map(msg => (
              <div
                key={msg.id}
                className={`alert ${
                  msg.success ? 'bg-body' : 'alert-warning'
                } mb-2 py-2 px-3`}
                role="alert"
              >
                <div className="d-flex flex-column">
                  <small className="text-muted fs-10">
                    {msg.timestamp.toLocaleTimeString()}
                  </small>
                  <div className="fs-9 text-primary lh-1">{msg.message}</div>
                  {msg.details && (
                    <small className="mt-1 text-muted fs-10 lh-1">
                      {msg.details}
                    </small>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  };

  const renderCustomerForm = () => (
    <Form onSubmit={handleSubmit} className="w-100">
      <div className="d-flex flex-column gap-2">
        <Form.Floating className="flex-grow-1">
          <Form.Select
            className="shadow-sm"
            name="customerId"
            value={formData.customerId}
            onChange={handleInputChange}
            isValid={isCustomerIdValidated}
          >
            <option value="">Select Customer ID</option>
            <option value="CLI-123">CLI-123</option>
            <option value="CLI-456">CLI-456</option>
            <option value="CLI-789">CLI-789</option>
          </Form.Select>
          <label>Customer ID</label>
        </Form.Floating>
        <div className="d-flex gap-2">
          <Form.Floating className="flex-grow-1">
            <Form.Control
              className="shadow-sm"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Email"
              isValid={isEmailValidated}
            />
            <label>Email</label>
          </Form.Floating>

          <Button type="submit" variant="primary" style={{ minWidth: '100px' }}>
            Update
          </Button>
        </div>
      </div>
    </Form>
  );

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="xl"
      animation={true}
      contentClassName="border-0"
    >
      <Modal.Header closeButton>
        <Modal.Title className="text-body-emphasis">
          {`${title} - ${agentName}`}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0 pe-3 bg-body">
        <Row className="g-0">
          <h3 className="pb-0 pt-4 px-6">Test scenario</h3>
          <p className="fs-8 lh-sm px-6 mb-0">
            This test scenario demonstrates the interaction between the agent,
            the customer, and the system. The agent starts the call without any
            prior information, which is provided incrementally as needed during
            the interaction only if Customer ID and email are verified first.
          </p>
        </Row>
        <Row className="g-0">
          <div className="px-6 pe-4">
            <ProductsList />
          </div>
        </Row>
        <Row className="g-0">
          {/* Columna izquierda: Info inicial y solicitudes del agente durante la conversación */}
          <Col md={4} className="p-4 px-6 pe-0 pt-0">
            <h5 className="pb-0 pt-2">Client data to verify during the call</h5>
            <p className="fs-9 lh-sm mb-2">
              The Customer ID and email are not known to the agent. The agent
              will only collect the information provided by the customer and ask
              for validation to the system.
            </p>
            {renderCustomerForm()}
            <div className="mt-0">
              <Row>
                <OrdersList />
              </Row>
            </div>
          </Col>

          {/* Columna centro: Imagen */}
          <Col md={2} className="d-flex p-4 pt-0 pe-0 pb-0 align-items-end">
            {agentImage && (
              <div className="text-center mb-2">
                <img
                  src={agentImage}
                  alt={agentName}
                  className="img-fluid rounded"
                  style={{
                    width: '100%',
                    minWidth: '163px',
                    maxWidth: '163px',
                    height: 'auto'
                  }}
                />
              </div>
            )}
          </Col>

          {/* Columna derecha: Datos dinámicos, widget de Conversación e información dinámica */}
          <Col md={6} className="p-4 pt-0">
            <h5 className="pb-0 pt-2">Call with {agentName}</h5>
            <p className="fs-9 lh-sm mb-2">
              Press the button below to start a conversation with the agent.
            </p>
            <HVADynamicConversationWidget
              agentId={agentCode}
              agentName={agentName}
              onError={onError}
              onFeedback={addFeedbackMessage}
              firstMessage={firstMessage}
              prompt={prompt}
              voice={voice}
              language={language}
            />
            {renderFeedbackPanel()}
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="primary" onClick={handleCloseModal}>
          Close and reset
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default HVADynamicConversationModalContent;
